import { Beer } from '../../state/types';

export type AnalyticsEventName =
  | 'beer_viewed'
  | 'beer_favourited'
  | 'beer_unfavourited'
  | 'beer_rating_set'
  | 'beer_tasting_list_added'
  | 'beer_tasting_list_removed'
  | 'beer_notes_saved'
  | 'tasting_list_bulk_add'
  | 'filter_applied'
  | 'filter_cleared'
  | 'search_submitted'
  | 'tab_changed'
  | 'brewery_detail_opened'
  | 'brewery_link_clicked'
  | 'onboarding_started'
  | 'onboarding_completed';

export type AnalyticsEventParams = Record<string, string | number | boolean | undefined>;

export interface AnalyticsEvent {
  name: AnalyticsEventName;
  params: AnalyticsEventParams;
}

const buildBeerBaseParams = (beer: Beer) => ({
  beer_id: beer.id,
  beer_name: beer.name,
  brewery: beer.brewery,
  style: beer.style,
  abv: beer.abv,
  beer_number: beer.number
});

export const buildBeerViewedEvent = (beer: Beer): AnalyticsEvent => ({
  name: 'beer_viewed',
  params: buildBeerBaseParams(beer)
});

export const buildBeerFavouritedEvent = (beer: Beer): AnalyticsEvent => ({
  name: 'beer_favourited',
  params: buildBeerBaseParams(beer)
});

export const buildBeerUnfavouritedEvent = (beer: Beer): AnalyticsEvent => ({
  name: 'beer_unfavourited',
  params: buildBeerBaseParams(beer)
});

export const buildBeerRatingSetEvent = (beer: Beer, rating: number): AnalyticsEvent => ({
  name: 'beer_rating_set',
  params: {
    ...buildBeerBaseParams(beer),
    rating
  }
});

export const buildBeerTastingListAddedEvent = (beer: Beer): AnalyticsEvent => ({
  name: 'beer_tasting_list_added',
  params: buildBeerBaseParams(beer)
});

export const buildBeerTastingListRemovedEvent = (beer: Beer): AnalyticsEvent => ({
  name: 'beer_tasting_list_removed',
  params: buildBeerBaseParams(beer)
});

export const buildBeerNotesSavedEvent = (beer: Beer, notesLength: number): AnalyticsEvent => ({
  name: 'beer_notes_saved',
  params: {
    ...buildBeerBaseParams(beer),
    notes_length: notesLength
  }
});

export const buildFilterAppliedEvent = (filterType: string, filterValue: string): AnalyticsEvent => ({
  name: 'filter_applied',
  params: {
    filter_type: filterType,
    filter_value: filterValue
  }
});

export const buildFilterClearedEvent = (filterType: string): AnalyticsEvent => ({
  name: 'filter_cleared',
  params: {
    filter_type: filterType
  }
});

export const buildSearchSubmittedEvent = (searchTerm: string): AnalyticsEvent => ({
  name: 'search_submitted',
  params: {
    search_term: searchTerm
  }
});

export const buildTabChangedEvent = (tabName: string): AnalyticsEvent => ({
  name: 'tab_changed',
  params: {
    tab_name: tabName
  }
});

export const buildBreweryDetailOpenedEvent = (breweryName: string): AnalyticsEvent => ({
  name: 'brewery_detail_opened',
  params: {
    brewery: breweryName
  }
});

export const buildBreweryLinkClickedEvent = (breweryName: string, linkType: 'website' | 'shop' | 'maps'): AnalyticsEvent => ({
  name: 'brewery_link_clicked',
  params: {
    brewery: breweryName,
    link_type: linkType
  }
});

export const buildOnboardingStartedEvent = (): AnalyticsEvent => ({
  name: 'onboarding_started',
  params: {}
});

export const buildOnboardingCompletedEvent = (): AnalyticsEvent => ({
  name: 'onboarding_completed',
  params: {}
});

export const buildTastingListBulkAddEvent = (count: number): AnalyticsEvent => ({
  name: 'tasting_list_bulk_add',
  params: {
    count,
    filter_type: 'filtered_beers'
  }
});
